import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Layers, MessageCircle, User, Shield, Coins } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SupportModal from "./SupportModal";

export function MobileFrame({ children }) {
  return (
    <div className="min-h-[100dvh] bg-[#09080E] text-slate-100 flex items-center justify-center p-0 sm:p-4 md:p-6 font-sans selection:bg-rose-500 selection:text-white">
      <div className="w-full max-w-md h-[100dvh] sm:h-[860px] bg-[#0D0C12] sm:rounded-[2rem] sm:border sm:border-white/10 sm:shadow-[0_25px_60px_-15px_rgba(255,46,99,0.25)] overflow-hidden flex flex-col relative">
        {children}
      </div>
    </div>
  );
}

export function Brand({ size = "md", onClick }) {
  const dims = size === "lg" ? "h-24" : "h-10";
  return (
    <button onClick={onClick} className="flex items-center group" data-testid="brand-logo">
      <img src="/gulmatch-logo.png" alt="GulMatch" className={`${dims} w-auto object-contain drop-shadow-[0_0_14px_rgba(255,46,99,0.35)]`} />
    </button>
  );
}

export function TopBar({ title, right, onBrandClick }) {
  return (
    <header className="h-16 px-4 bg-[#0D0C12]/90 backdrop-blur-xl border-b border-white/5 flex items-center justify-between z-30 shrink-0">
      {title ? (
        <h1 className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h1>
      ) : (
        <Brand onClick={onBrandClick} />
      )}
      <div className="flex items-center gap-2">{right}</div>
    </header>
  );
}

export function BottomNav() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [showEarning, setShowEarning] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/unread-count");
        if (active) setUnread(data.total);
      } catch {}
    };
    load();
    const t = setInterval(load, 6000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const items = [
    { to: "/", icon: Layers, label: "Discover", testid: "nav-tab-swipe" },
    { to: "/matches", icon: MessageCircle, label: "Matches", testid: "nav-tab-matches" },
    { action: "earning", icon: Coins, label: "Earning", testid: "nav-tab-earning" },
    { to: "/profile", icon: User, label: "Profile", testid: "nav-tab-profile" },
  ];
  if (user && typeof user === "object" && user.is_admin) {
    items.push({ to: "/admin", icon: Shield, label: "Admin", testid: "nav-tab-admin" });
  }
  return (
    <>
      <nav className="h-[4.25rem] px-6 bg-[#13101C]/95 backdrop-blur-xl border-t border-white/10 flex items-center justify-around z-30 shrink-0">
        {items.map((it) => it.action === "earning" ? (
          <button
            key="earning"
            data-testid={it.testid}
            onClick={() => setShowEarning(true)}
            className="flex flex-col items-center gap-1 w-16 relative"
          >
            <div className="relative">
              <it.icon className="w-6 h-6 transition-colors text-slate-500" strokeWidth={2.2} />
            </div>
            <span className="text-[10px] font-semibold text-slate-500">{it.label}</span>
          </button>
        ) : (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            data-testid={it.testid}
            onClick={() => navigate(it.to)}
            className="flex flex-col items-center gap-1 w-16 relative"
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <it.icon className={`w-6 h-6 transition-colors ${isActive ? "text-rose-500" : "text-slate-500"}`} strokeWidth={2.2} />
                  {it.to === "/matches" && unread > 0 && (
                    <span data-testid="nav-unread-badge" className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center border-2 border-[#13101C]">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-semibold ${isActive ? "text-rose-400" : "text-slate-500"}`}>{it.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
      {showEarning && <SupportModal onClose={() => setShowEarning(false)} />}
    </>
  );
}
